// CSV / Excel import and export of garments, fabrics and trims (Settings → Data Import & Export).
// Column names match FALCON PLM v2.0.41's CSV files, so its exports can be imported directly.
import { FABRIC_COMP_FIELDS, type ItemType } from "./items.js";

export type FieldKind = "text" | "list" | "number" | "bool";
export interface TransferField { key: string; label: string; kind: FieldKind; required?: boolean; aliases?: string[]; note?: string }

const PACKING: TransferField[] = [
  { key: "pk_pieceWeightKg", label: "Piece Weight KG", kind: "number", aliases: ["pieceweight", "unitweightkg", "garmentweightkg"] },
  { key: "pk_pcsPerCarton", label: "Pieces per Carton", kind: "number", aliases: ["pcspercarton", "pcscarton", "cartonqty", "piecescarton"] },
  { key: "pk_cartonLengthCm", label: "Carton Length CM", kind: "number", aliases: ["cartonlength", "lengthcm"] },
  { key: "pk_cartonWidthCm", label: "Carton Width CM", kind: "number", aliases: ["cartonwidth"] },
  { key: "pk_cartonHeightCm", label: "Carton Height CM", kind: "number", aliases: ["cartonheight", "heightcm"] },
  { key: "pk_cartonNetWeightKg", label: "Carton Net Weight KG", kind: "number", aliases: ["cartonnetweight", "netweightkg", "netweight"] },
  { key: "pk_cartonGrossWeightKg", label: "Carton Gross Weight KG", kind: "number", aliases: ["cartongrossweight", "grossweightkg", "grossweight"] },
];
const COMMON_END: TransferField[] = [
  { key: "discontinued", label: "Discontinued", kind: "bool", aliases: ["inactive", "obsolete", "archived"] },
  { key: "notes", label: "Notes", kind: "text" },
];
const PURCHASING: TransferField[] = [
  { key: "supplier", label: "Supplier", kind: "text", aliases: ["suppliername", "mill", "vendor"], note: "Matched to Settings → Suppliers by name." },
  { key: "unit", label: "Unit", kind: "text", aliases: ["uom"] },
  { key: "unitCost", label: "Cost per Unit", kind: "number", aliases: ["unitcost", "cost", "price per unit"] },
  { key: "price", label: "Selling Price per Unit", kind: "number", aliases: ["sellingprice", "price"] },
];

export const TRANSFER_FIELDS: Record<ItemType, TransferField[]> = {
  product: [
    { key: "code", label: "Product Code", kind: "text", required: true, aliases: ["code", "itemcode", "stylecode", "sku", "productid", "productno", "productnumber"] },
    { key: "name", label: "Product Name", kind: "text", aliases: ["name", "itemname", "stylename", "productdescription"], note: "Used when there is no Custom Name; otherwise the name is built automatically." },
    { key: "brand", label: "Brand", kind: "text", aliases: ["brandname"] },
    { key: "category", label: "Category", kind: "text", aliases: ["productcategory", "producttype"] },
    { key: "customName", label: "Custom Name", kind: "text" },
    { key: "fabricCode", label: "Fabric Code", kind: "text", aliases: ["fabricid", "fabric", "fabricref", "mainfabric"] },
    { key: "standards", label: "Standards", kind: "list", aliases: ["standard", "certifications", "certification"] },
    { key: "colours", label: "Colours", kind: "list", aliases: ["colour", "colors", "color", "availablecolours"] },
    { key: "sizes", label: "Sizes", kind: "list", aliases: ["size", "sizerange", "availablesizes"] },
    { key: "features", label: "Features", kind: "list", aliases: ["productfeatures", "feature"] },
    { key: "washCare", label: "Wash Care", kind: "list", aliases: ["care", "careinstructions"] },
    { key: "season", label: "Season", kind: "text", aliases: ["collection"] },
    { key: "gender", label: "Gender", kind: "text" },
    { key: "fit", label: "Fit", kind: "text" },
    ...PACKING,
    { key: "makingCost", label: "Making Cost", kind: "number", aliases: ["cmt", "cm"] },
    { key: "otherCost", label: "Other Costs", kind: "number" },
    { key: "price", label: "Selling Price", kind: "number", aliases: ["sellingprice"] },
    ...COMMON_END,
  ],
  fabric: [
    { key: "code", label: "Fabric Code", kind: "text", aliases: ["fabricid", "code"], note: "Leave empty to create the next FAB code." },
    { key: "speciality", label: "Speciality", kind: "text", aliases: ["specialty"] },
    { key: "cat", label: "CAT", kind: "text" },
    { key: "atpv", label: "ATPV", kind: "text" },
    { key: "cvc", label: "CVC", kind: "text" },
    ...FABRIC_COMP_FIELDS.map(([k, label]) => ({ key: `comp_${k}`, label, kind: "number" as const })),
    { key: "structure", label: "Structure", kind: "text" },
    { key: "gsm", label: "GSM", kind: "number", aliases: ["weight"] },
    { key: "widthCm", label: "Width CM", kind: "number", aliases: ["width"] },
    { key: "colour", label: "Colour", kind: "text", aliases: ["color"] },
    ...PURCHASING,
    ...COMMON_END,
  ],
  trim: [
    { key: "code", label: "Code", kind: "text", aliases: ["trimcode", "itemcode"], note: "Leave empty to create the next TRM code." },
    { key: "name", label: "Name", kind: "text", required: true, aliases: ["trimname", "description"] },
    { key: "category", label: "Type", kind: "text", aliases: ["trimtype", "category"] },
    { key: "colour", label: "Colour", kind: "text", aliases: ["color"] },
    { key: "size", label: "Size", kind: "text" },
    { key: "description", label: "Details", kind: "text" },
    ...PURCHASING,
    ...COMMON_END,
  ],
};
/** v2.0.41 fabric export columns that are calculated, so they are read but ignored. */
export const IGNORED_HEADERS = ["totalcomp", "totalcomposition", "fabriccomposition", "fabricname", "cartoncbm", "cbmpercarton"];

export const normalizeHeader = (v: unknown) => String(v ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

/** Suggest which file column goes with each field (exact name first, then known alternatives). */
export function suggestMapping(fields: TransferField[], headers: string[]): Record<string, number> {
  const norm = headers.map(normalizeHeader);
  const used = new Set<number>();
  const out: Record<string, number> = {};
  for (const pass of [0, 1]) {
    for (const f of fields) {
      if (out[f.key] !== undefined) continue;
      const names = pass === 0 ? [normalizeHeader(f.label)] : (f.aliases || []).map(normalizeHeader);
      const i = norm.findIndex((h, n) => !used.has(n) && names.includes(h));
      if (i >= 0) { out[f.key] = i; used.add(i); }
    }
  }
  return out;
}

/** Several values in one cell: "S | M | L", "S; M; L" or "S, M, L". */
export function splitMulti(v: string): string[] {
  const s = String(v ?? "").trim();
  if (!s) return [];
  const sep = s.includes("|") ? "|" : s.includes(";") ? ";" : ",";
  return [...new Set(s.split(sep).map((x) => x.trim()).filter(Boolean))];
}
export function parseBool(v: string): boolean {
  return ["1", "true", "yes", "y", "discontinued", "inactive", "x"].includes(String(v ?? "").trim().toLowerCase());
}
export function parseNumber(v: string): number | null {
  const s = String(v ?? "").trim().replace(/[£$€%\s]/g, "").replace(/,(?=\d{3}\b)/g, "");
  if (!s) return null;
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
}

/** Read CSV text (commas or semicolons, quoted cells, Excel's byte-order mark). */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, "");
  const firstLine = src.slice(0, src.indexOf("\n") >= 0 ? src.indexOf("\n") : src.length);
  const delim = (firstLine.match(/;/g) || []).length > (firstLine.match(/,/g) || []).length ? ";" : firstLine.includes("\t") && !firstLine.includes(",") ? "\t" : ",";
  const rows: string[][] = [];
  let row: string[] = [], cell = "", quoted = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++; } else quoted = false;
      } else cell += c;
    } else if (c === '"' && cell === "") quoted = true;
    else if (c === delim) { row.push(cell); cell = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && src[i + 1] === "\n") i++;
      row.push(cell); cell = "";
      if (row.some((x) => x.trim() !== "")) rows.push(row);
      row = [];
    } else cell += c;
  }
  row.push(cell);
  if (row.some((x) => x.trim() !== "")) rows.push(row);
  return rows;
}

export function csvLine(values: unknown[]): string {
  return values.map((v) => {
    let s = v === null || v === undefined ? "" : String(v);
    // Stop spreadsheet programs treating text such as "=SUM(...)" as a formula.
    if (/^[=+\-@]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s)) s = `'${s}`;
    return /[",\n\r']/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(",");
}

/** Undo the formula guard added on export. */
export const cleanCell = (v: unknown) => String(v ?? "").trim().replace(/^'(?=[=+\-@])/, "");

export interface ImportRowResult { row: number; code: string; status: "new" | "update" | "skip" | "error"; message: string }

// ---- Suppliers and PO lines (list pages → Import) ------------------------------------------------------------------
export const IMPORT_KINDS = ["product", "fabric", "trim", "supplier", "poline"] as const;
export type ImportKind = (typeof IMPORT_KINDS)[number];
export const IMPORT_KIND_LABEL: Record<ImportKind, string> = { product: "Garments", fabric: "Fabrics", trim: "Trims", supplier: "Suppliers", poline: "PO lines" };
export const EXTRA_TRANSFER_FIELDS: Record<"supplier" | "poline", TransferField[]> = {
  supplier: [
    { key: "companyName", label: "Company Name", kind: "text", required: true, aliases: ["supplier", "suppliername", "name", "company"], note: "Matched to existing suppliers by name." },
    { key: "address1", label: "Address Line 1", kind: "text", aliases: ["address", "address1"] },
    { key: "address2", label: "Address Line 2", kind: "text", aliases: ["address2"] },
    { key: "city", label: "City", kind: "text" },
    { key: "province", label: "Province / State", kind: "text", aliases: ["province", "state", "region"] },
    { key: "country", label: "Country", kind: "text" },
    { key: "telephone", label: "Telephone", kind: "text", aliases: ["phone", "tel"] },
    { key: "mobile", label: "Mobile", kind: "text", aliases: ["cell"] },
    { key: "email", label: "Email", kind: "text", aliases: ["emailaddress"] },
    { key: "owner", label: "Owner (email)", kind: "text", aliases: ["owner", "owneremail", "merchandiser"], note: "The email of the person in your team who looks after this supplier." },
    { key: "active", label: "Active", kind: "bool" },
    { key: "notes", label: "Notes", kind: "text" },
  ],
  poline: [
    { key: "poNumber", label: "PO Number", kind: "text", required: true, aliases: ["po", "ponumber", "purchaseorder"], note: "The PO must already exist and be open. The change becomes the PO's next revision." },
    { key: "code", label: "Product Code", kind: "text", required: true, aliases: ["code", "itemcode", "stylecode", "fabriccode", "trimcode"] },
    { key: "colour", label: "Colour", kind: "text", aliases: ["color"] },
    { key: "sizes", label: "Quantities by Size", kind: "text", aliases: ["sizes", "sizequantities", "qtybysize", "sizeqty"], note: "Garments: e.g. S=100 | M=200 | L=150." },
    { key: "quantity", label: "Quantity", kind: "number", aliases: ["qty", "metres", "meters"], note: "Fabrics and trims (metres or units)." },
    { key: "unitPrice", label: "Unit Price", kind: "number", aliases: ["price", "unitcost", "fob"] },
  ],
};
/** "S=100 | M=200" (also "S:100; M:200") → { S: 100, M: 200 }; throws a readable message when a quantity isn't a whole number. */
export function parseSizeQuantities(v: string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const part of splitMulti(v)) {
    const m = part.match(/^(.+?)\s*[=:]\s*([0-9.,\s]+)$/);
    if (!m) throw new Error(`"${part}" should look like S=100.`);
    const n = Number(m[2].replace(/[,\s]/g, ""));
    if (!Number.isInteger(n) || n < 0) throw new Error(`Size ${m[1].trim()}: "${m[2].trim()}" isn't a whole number.`);
    out[m[1].trim()] = n;
  }
  return out;
}
export interface ImportSettings { merchandiserKinds: ImportKind[] }
export function normalizeImportSettings(v: any): ImportSettings {
  return { merchandiserKinds: Array.isArray(v?.merchandiserKinds) ? IMPORT_KINDS.filter((k) => v.merchandiserKinds.includes(k)) : [] };
}
export const UNDO_HOURS = 24;
